import React from 'react';
import {
	Box,
	Button,
	CardContent,
	CircularProgress,
	Typography,
	useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { PageHeader } from '@/components/layouts/PageHeader';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { FolioDrawerFooter } from '@/components/Drawers/FolioDrawer/FolioDrawerFooter';
import { FolioDrawerFormGuard } from '@/components/Drawers/FolioDrawer/FolioDrawerFormGuard';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import { showToast, hideToast } from '@/features/toast/toastSlice';
import {
	useCreateRelationshipContactMutation,
	useUpdateRelationshipContactMutation,
} from '@/features/relationshipContacts/relationshipContactsApi';
import validationSchema from './validationSchema';
import { RelationshipsIcon } from '@/components/icons/RelationshipsIcon';
import { toRelationshipContactInitialValues } from './fields';
import RelationshipContactFormFields from './Fields/RelationshipContactFormFields';
import { useObjectContext } from '@/hooks/useObjectContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';

const CreateRelationshipContactForm = ({ dialogView = false, onSubmit }) => {
	const theme = useTheme();
	const dispatch = useDispatch();

	const [createRelationshipContact, { isLoading: isCreatingRelationshipContact }] =
		useCreateRelationshipContactMutation();
	const [updateRelationshipContact, { isLoading: isLinkingRelationshipContact }] =
		useUpdateRelationshipContactMutation();
	const { relationshipCompany } = useObjectContext();
	const { getFeatureFlags } = useFeatureFlagContext();
	const isRelationshipContactsEnabled = Boolean(getFeatureFlags('relationshipContacts')?.active);

	const disabled = isCreatingRelationshipContact || isLinkingRelationshipContact;

	const icon = (
		<RelationshipsIcon
			sx={{
				fontSize: dialogView ? '25px' : '40px',
			}}
			width={dialogView ? '25px' : '40px'}
			height={dialogView ? '25px' : '40px'}
			{...(theme.palette.mode === 'dark' && {
				bgColor: 'transparent',
			})}
		/>
	);

	return (
		<Formik
			initialValues={{
				...toRelationshipContactInitialValues(
					isRelationshipContactsEnabled
						? {
								relationshipCompanyId: relationshipCompany?.id,
								relationshipCompanyName: relationshipCompany?.name,
							}
						: undefined,
				),
				isExistingRelationshipContact: false,
				existingRelationshipContact: null,
			}}
			validationSchema={validationSchema}
			enableReinitialize
			validateOnBlur
			validateOnChange={false}
			onSubmit={async (values, { resetForm, setSubmitting }) => {
				const toastId = Math.random();
				const isExisting = Boolean(
					values.isExistingRelationshipContact && values.existingRelationshipContact?.id,
				);
				dispatch(
					showToast({
						message: isExisting ? 'Associating contact...' : 'Creating contact...',
						autohide: false,
						loading: true,
						id: toastId,
					}),
				);

				try {
					let result;
					if (isExisting) {
						result = await updateRelationshipContact({
							relationshipContactId: values.existingRelationshipContact.id,
							relationshipCompanyId: values.relationshipCompanyId,
							isPrimary: values.isPrimary,
						}).unwrap();
					} else {
						const properties = {
							firstName: values.firstName?.trim(),
							lastName: values.lastName?.trim(),
							email: values.email?.trim(),
							relationshipType: values.relationshipType,
							relationshipStatus: values.relationshipStatus,
							roleTitle: values.roleTitle?.trim(),
							mobile: values.mobile?.trim(),
							notesSummary: values.notesSummary?.trim(),
							nextActionDescription: values.nextActionDescription?.trim(),
							lastContactedAt: values.lastContactedAt,
							nextActionDate: values.nextActionDate,
						};
						result = await createRelationshipContact({
							properties,
							relationshipCompanyId: values.relationshipCompanyId,
							isPrimary: values.isPrimary,
						}).unwrap();
					}

					dispatch(hideToast(toastId));
					dispatch(
						showToast({
							message: isExisting ? 'Contact associated!' : 'Contact created!',
							success: true,
							autohide: true,
							duration: 6000,
						}),
					);
					onSubmit?.(result?.id);
					resetForm();
				} catch (error) {
					dispatch(hideToast(toastId));
					dispatch(
						showToast({
							message:
								error?.data?.message ??
								error?.message ??
								(isExisting
									? 'Unable to associate contact.'
									: 'Unable to create contact.'),
							error: true,
						}),
					);
				} finally {
					setSubmitting(false);
				}
			}}
		>
			{({ isSubmitting, handleSubmit, validateForm }) => {
				const isDisabled = isSubmitting || disabled;

				return (
					<Form
						style={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							minHeight: 0,
						}}
					>
						{dialogView && (
							<FolioDrawerHeader title='Create New Relationship' icon={icon} />
						)}
						<Box
							sx={{
								flex: 1,
								overflowY: 'auto',
								...(!dialogView && {
									paddingBottom: '2em',
								}),
							}}
						>
							<Box sx={{ pt: dialogView ? 0 : 3 }}>
								{!dialogView && (
									<PageHeader title='Create New Relationship' icon={icon} />
								)}

								<FormikErrorScroll isSubmitting={isSubmitting} />

								<CardContent>
									<Typography variant='body1' mb='1.15em'>
										Fill out the following form to create your new contact.
									</Typography>
									<RelationshipContactFormFields
										disabled={isDisabled}
										allowExistingIndividual={isRelationshipContactsEnabled}
									/>
								</CardContent>
							</Box>
						</Box>
						<FolioDrawerFormGuard />
						<FolioDrawerFooter>
							<Button
								color='primary'
								type='button'
								onClick={() => {
									handleSubmit();
									setTimeout(() => {
										validateForm();
									}, 100);
								}}
								variant='contained'
								disabled={isDisabled}
							>
								{isDisabled ? (
									<CircularProgress size='2em' sx={{ color: '#ffffff' }} />
								) : (
									'Submit'
								)}
							</Button>
						</FolioDrawerFooter>
					</Form>
				);
			}}
		</Formik>
	);
};

export default CreateRelationshipContactForm;
